/**
 * Wall Breaker — self-contained soccer free-kick game runtime hosted in a react-native-webview.
 * You strike a football off the spot (swipe to aim + power, release to kick) to topple a stacked wall.
 *
 * Why a string module: the whole game (canvas render loop + physics + audio + levels) ships with the app
 * as one HTML document so it runs without any RN-bundle imports. Matter.js is loaded from a pinned CDN
 * with SRI; WKWebView/Android WebView cache it after the first successful load, so subsequent opens work
 * offline (a cold-cache offline first-load is the only gap — see spec AC-7 / plan follow-up to bundle it).
 *
 * Bridge protocol (window.ReactNativeWebView.postMessage, JSON):
 *   web → RN:  { type:'ready' } | { type:'level-clear', level, ballsLeft, stars }
 *              | { type:'level-failed', level } | { type:'complete', level }
 *   RN → web:  window.__slingshotApplyStart({ levelCount, ballsPerLevel, startLevel })
 *
 * Coordinate space is a fixed 1280×720 landscape world, CSS-scaled to fit the WebView (letterboxed) so we
 * never rotate the device — the playfield adapts to whatever box RN gives it.
 *
 * Copy guard: no "bet" (neutral wording), no "World Cup 2026" (generic football framing).
 */

const MATTER_CDN_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/matter-js/0.20.0/matter.min.js';
const MATTER_SRI = 'sha512-6+7rTBmR6pRFe9fa0vCFjFaHZj/XYa7774bEBzRtxgdpIJOS++R3cKd6Prg/eJmxtsJotd8KAg4g57uuVQsZKA==';

export function buildSlingshotGameHtml(): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover" />
<style>
  :root { --accent:#FB923C; --ink:#0F0A1A; }
  * { margin:0; padding:0; box-sizing:border-box; -webkit-tap-highlight-color:transparent; }
  html,body { width:100%; height:100%; overflow:hidden; background:var(--ink); touch-action:none; }
  #stage { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; }
  canvas { display:block; max-width:100%; max-height:100%; }
  #hud { position:absolute; top:10px; left:0; right:0; display:flex; justify-content:space-between;
    padding:0 16px; font:600 16px/1 -apple-system,system-ui,sans-serif; color:#F7FAFC; pointer-events:none;
    text-shadow:0 1px 3px rgba(0,0,0,.55); }
  /* Clear the native back button (top-left, ~36px) so the level label never sits under it. */
  #hud #level { padding-left:42px; }
  #hud .balls { display:flex; gap:6px; align-items:center; }
  #hud .dot { width:14px; height:14px; border-radius:50%; background:radial-gradient(circle at 35% 30%, #fff, #cfcfcf 55%, #8d8d8d);
    box-shadow:0 1px 2px rgba(0,0,0,.5); }
  #hud .dot.used { opacity:.25; }
  #task { position:absolute; top:40px; left:50%; transform:translateX(-50%); display:flex; align-items:center;
    gap:8px; background:rgba(15,10,26,.7); border:1.5px solid rgba(255,255,255,.15); border-radius:10px;
    padding:5px 14px; font:600 13px/1 -apple-system,system-ui,sans-serif; color:#F7FAFC; pointer-events:none;
    text-shadow:0 1px 2px rgba(0,0,0,.4); backdrop-filter:blur(4px); }
  #task .flag-chip { width:28px; height:19px; border-radius:3px; border:1px solid rgba(255,255,255,.25);
    overflow:hidden; display:flex; flex-shrink:0; position:relative; }
  #task .flag-chip.scored { opacity:.4; }
  #task .flag-chip .flag-chip-img { position:absolute; inset:0; width:100%; height:100%; object-fit:cover; }
  #task .flag-chip .stripe { flex:1; height:100%; }
  #task .flag-chip.v { flex-direction:row; }
  #task .flag-chip:not(.v) { flex-direction:column; }
  #task .joker-warn { color:#F87171; font-weight:800; margin-left:4px; }
  #task .reward { color:#FCD34D; font-weight:800; margin-left:6px; }
  #coins { position:absolute; top:12px; right:100px;
    font:800 16px/1 -apple-system,system-ui,sans-serif; color:#FCD34D; pointer-events:none;
    text-shadow:0 1px 4px rgba(0,0,0,.6); display:flex; align-items:center; gap:5px; }
  #coins .coin-icon { width:16px; height:16px; border-radius:50%; background:radial-gradient(circle at 35% 30%,#FDE68A,#F59E0B 60%,#B45309);
    border:1.5px solid #92400E; box-shadow:0 1px 2px rgba(0,0,0,.4); }
  /* Campaign complete overlay */
  #campaign-done { position:fixed; inset:0; background:rgba(15,10,26,.88); display:flex;
    align-items:center; justify-content:center; z-index:100; }
  .cd-box { text-align:center; color:#F7FAFC; font-family:-apple-system,system-ui,sans-serif; }
  .cd-title { font-size:28px; font-weight:900; margin-bottom:8px; }
  .cd-sub { font-size:16px; font-weight:600; color:#9CA3AF; margin-bottom:12px; }
  .cd-coins { font-size:22px; font-weight:800; color:#FCD34D; margin-bottom:16px; }
  .cd-lock { font-size:14px; font-weight:600; color:#FB923C; background:rgba(251,146,60,.12);
    border:1px solid rgba(251,146,60,.3); border-radius:8px; padding:8px 18px; display:inline-block; }
  /* Landscape: fill viewport width, shrink HUD text */
  @media (orientation: landscape) {
    canvas { max-width:100vw; max-height:100vh; width:100vw; }
    #hud { font-size:14px; padding:0 10px; top:6px; }
    #task { top:30px; padding:4px 10px; font-size:12px; }
    #task .flag-chip { width:24px; height:16px; }
    #coins { top:8px; right:80px; font-size:14px; }
  }
  #toast { position:absolute; bottom:18px; left:50%; transform:translateX(-50%);
    font:700 18px -apple-system,system-ui,sans-serif; color:#FDE68A; text-shadow:0 2px 6px rgba(0,0,0,.6);
    opacity:0; transition:opacity .25s; pointer-events:none; }
  #toast.show { opacity:1; }
</style>
</head>
<body>
  <div id="stage"><canvas id="c" width="1280" height="720"></canvas></div>
  <div id="hud"><div id="level"></div><div class="balls" id="balls"></div></div>
  <div id="coins"><div class="coin-icon"></div><span id="coin-count">0</span></div>
  <div id="task"></div>
  <div id="toast"></div>
<script src="${MATTER_CDN_SRC}" integrity="${MATTER_SRI}" crossorigin="anonymous" referrerpolicy="no-referrer"></script>
<script>
(function () {
  var W = 1280, H = 720, GROUND_Y = 632, CRATE = 52;
  // Bridge to the RN host (native WebView) or the embedding page (web iframe).
  var post = function (m) {
    var s = JSON.stringify(m);
    try { if (window.ReactNativeWebView) { window.ReactNativeWebView.postMessage(s); return; } } catch (e) {}
    try { if (window.parent && window.parent !== window) window.parent.postMessage(s, '*'); } catch (e) {}
  };

  // ---- audio (synthesized; no asset files) -------------------------------------------------
  var AC = null;
  function tone(freq, dur, type, gain) {
    try {
      if (!AC) AC = new (window.AudioContext || window.webkitAudioContext)();
      var o = AC.createOscillator(), g = AC.createGain();
      o.type = type || 'sine'; o.frequency.value = freq;
      g.gain.value = gain || 0.05; o.connect(g); g.connect(AC.destination);
      var t = AC.currentTime; o.start(t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur); o.stop(t + dur);
    } catch (e) {}
  }
  var sfx = {
    launch: function () { tone(220, 0.18, 'triangle', 0.06); },
    hit: function () { tone(120, 0.08, 'square', 0.04); },
    boom: function () { tone(60, 0.35, 'sawtooth', 0.08); setTimeout(function(){tone(45,0.2,'square',0.06);},80); },
    clear: function () { tone(523, 0.12, 'sine', 0.06); setTimeout(function(){tone(784,0.18,'sine',0.06);},110); },
    fail: function () { tone(160, 0.25, 'sawtooth', 0.05); }
  };

  // ---- Matter setup ------------------------------------------------------------------------
  var M = window.Matter;
  if (!M) { post({ type: 'load-error' }); return; }
  var Engine = M.Engine, World = M.World, Bodies = M.Bodies, Body = M.Body,
      Composite = M.Composite, Constraint = M.Constraint, Events = M.Events, Vector = M.Vector;

  var engine = Engine.create();
  engine.gravity.y = 1.0;
  var world = engine.world;
  var canvas = document.getElementById('c'), ctx = canvas.getContext('2d');

  // static scenery
  var ground = Bodies.rectangle(W / 2, GROUND_Y + 44, W + 200, 120, { isStatic: true, label: 'ground' });
  var leftWall = Bodies.rectangle(-30, H / 2, 60, H * 2, { isStatic: true });
  World.add(world, [ground, leftWall]);

  // ---- config / state ----------------------------------------------------------------------
  var cfg = { levelCount: 50, ballsPerLevel: 3, startLevel: 1 };
  var RETRY_COST = 1, COINS_PER_LEVEL = 1, SESSION_COIN_CAP = 50, COOLDOWN_MS = 30 * 60 * 1000;
  function isBossLevel(n) { return n % 10 === 0; }
  function rewardForLevel(n) { return COINS_PER_LEVEL; }
  var level = 1, ballsLeft = 3, crates = [], targets = [], jokers = [], bombs = [], ball = null;
  var totalCoins = 0, coinsEarnedSession = 0; // visual only — server is authoritative
  var campaignDone = false, cooldownUntil = 0; // cooldown timestamp (ms since epoch)
  // Environmental hazards (per level, cleared on buildLevel)
  var platforms = [];  // static mid-air floors
  var fireZones = [];  // { x, y, w } — destroy flags, fail if touched
  var windZones = [];  // { x, y, w, h, force } — push bodies sideways
  var aiming = false, launched = false, settling = false, levelOver = false, framesLaunched = 0;
  var aimFrom = null, aimTo = null;                 // swipe vector (press → current) drives aim + power
  var KICK = { x: 190, y: GROUND_Y - 30 };          // free-kick spot — ball rests here on the pitch
  var MAX_DRAG = 240, MAX_SPEED = 26, MIN_POWER = 0.08;
  var BALL_AIR = 0.006;                              // ball frictionAir — MUST match the aim preview
  var FIXED_DT = 1000 / 60;                          // fixed physics step so the preview is deterministic
  function kickVelocity() {
    if (!aimFrom || !aimTo) return { x: 0, y: 0, power: 0 };
    var dx = aimTo.x - aimFrom.x, dy = aimTo.y - aimFrom.y, d = Math.hypot(dx, dy);
    if (d < 1) return { x: 0, y: 0, power: 0 };
    var power = Math.min(d, MAX_DRAG) / MAX_DRAG, sp = power * MAX_SPEED;
    return { x: dx / d * sp, y: dy / d * sp, power: power };
  }

  // ---- country flags — all 48 Football 2026 qualified nations -------------------------------
  // Real flag art is loaded from the CDN (assets.zporti.com/assets/Flags/{code}.svg). The stripe
  // colours (c/v/mark) are kept as an instant fallback shown until each SVG finishes loading (or if
  // it fails). code = flag-icons path segment (alpha-2, or gb-eng / gb-sct for the home nations).
  var FLAG_CDN = 'https://assets.zporti.com/assets/Flags/';
  var FLAGS = [
    // Hosts
    { code:'ca', name:'Canada',      c:['#FF0000','#FFFFFF','#FF0000'], v:true, mark:'🍁' },
    { code:'mx', name:'Mexico',      c:['#006847','#FFFFFF','#CE1126'], v:true },
    { code:'us', name:'USA',         c:['#B22234','#FFFFFF','#3C3B6E'] },
    // Europe (16)
    { code:'at', name:'Austria',     c:['#ED2939','#FFFFFF','#ED2939'] },
    { code:'be', name:'Belgium',     c:['#000','#FDDA24','#EF3340'], v:true },
    { code:'ba', name:'Bosnia',      c:['#002395','#FECB00','#002395'] },
    { code:'hr', name:'Croatia',     c:['#FF0000','#FFFFFF','#171796'] },
    { code:'cz', name:'Czechia',     c:['#FFFFFF','#D7141A','#11457E'] },
    { code:'gb-eng', name:'England', c:['#FFFFFF','#CE1124','#FFFFFF'], mark:'+' },
    { code:'fr', name:'France',      c:['#002395','#FFFFFF','#ED2939'], v:true },
    { code:'de', name:'Germany',     c:['#000','#DD0000','#FFCC00'] },
    { code:'nl', name:'Netherlands', c:['#AE1C28','#FFFFFF','#21468B'] },
    { code:'no', name:'Norway',      c:['#BA0C2F','#002868','#FFFFFF'] },
    { code:'pt', name:'Portugal',    c:['#006600','#FF0000','#FF0000'], v:true },
    { code:'gb-sct', name:'Scotland',c:['#005EB8','#FFFFFF','#005EB8'], mark:'X' },
    { code:'es', name:'Spain',       c:['#AA151B','#F1BF00','#AA151B'] },
    { code:'se', name:'Sweden',      c:['#004B87','#FECC00','#004B87'] },
    { code:'ch', name:'Switzerland', c:['#FF0000','#FFFFFF','#FF0000'], mark:'+' },
    { code:'tr', name:'Türkiye',     c:['#E30A17','#FFFFFF','#E30A17'], mark:'C' },
    // South America (6)
    { code:'ar', name:'Argentina',   c:['#74ACDF','#FFFFFF','#74ACDF'] },
    { code:'br', name:'Brazil',      c:['#009739','#FEDD00','#009739'] },
    { code:'co', name:'Colombia',    c:['#FCD116','#FCD116','#003893'] },
    { code:'ec', name:'Ecuador',     c:['#FFD100','#0033A0','#CE1126'] },
    { code:'py', name:'Paraguay',    c:['#D52B1E','#FFFFFF','#0038A8'] },
    { code:'uy', name:'Uruguay',     c:['#0038A8','#FFFFFF','#0038A8'] },
    // Asia (9)
    { code:'au', name:'Australia',   c:['#00008B','#FFFFFF','#00008B'] },
    { code:'iq', name:'Iraq',        c:['#CE1126','#FFFFFF','#000'] },
    { code:'ir', name:'Iran',        c:['#239F40','#FFFFFF','#DA0000'] },
    { code:'jp', name:'Japan',       c:['#FFFFFF','#BC002D','#FFFFFF'], mark:'O' },
    { code:'jo', name:'Jordan',      c:['#000','#FFFFFF','#007A3D'] },
    { code:'kr', name:'Korea',       c:['#FFFFFF','#CD2E3A','#0047A0'] },
    { code:'qa', name:'Qatar',       c:['#8D1B3D','#FFFFFF','#8D1B3D'] },
    { code:'sa', name:'Saudi Arabia',c:['#006C35','#FFFFFF','#006C35'] },
    { code:'uz', name:'Uzbekistan',  c:['#0099B5','#FFFFFF','#1EB53A'] },
    // Africa (10)
    { code:'dz', name:'Algeria',     c:['#006633','#FFFFFF','#D21034'], v:true },
    { code:'cv', name:'Cabo Verde',  c:['#003893','#FFFFFF','#CE1126'] },
    { code:'cd', name:'Congo DR',    c:['#007FFF','#CE1021','#F7D618'] },
    { code:'ci', name:'Ivory Coast', c:['#FF8200','#FFFFFF','#009A44'], v:true },
    { code:'eg', name:'Egypt',       c:['#CE1126','#FFFFFF','#000'] },
    { code:'gh', name:'Ghana',       c:['#CE1126','#FCD116','#006B3F'] },
    { code:'ma', name:'Morocco',     c:['#C1272D','#006233','#C1272D'] },
    { code:'sn', name:'Senegal',     c:['#00853F','#FECB00','#E31B23'], v:true },
    { code:'za', name:'South Africa',c:['#FFB81C','#007749','#DE3831'] },
    { code:'tn', name:'Tunisia',     c:['#E70013','#FFFFFF','#E70013'], mark:'C' },
    // CONCACAF (3 more)
    { code:'cw', name:'Curaçao',     c:['#002B7F','#F9E814','#002B7F'] },
    { code:'ht', name:'Haiti',       c:['#00209F','#D21034','#00209F'] },
    { code:'pa', name:'Panama',      c:['#FFFFFF','#DA121A','#0069B4'] },
    // Oceania (1)
    { code:'nz', name:'New Zealand', c:['#00247D','#CC142B','#00247D'] },
  ];

  // Lazy flag-image cache — an <img> per code, drawn to the canvas once decoded (else stripes show).
  var flagImgCache = {};
  function flagImage(code) {
    if (!code) return null;
    var img = flagImgCache[code];
    if (!img) { img = new Image(); img.decoding = 'async'; img.src = FLAG_CDN + code + '.svg'; flagImgCache[code] = img; }
    return img;
  }

  // Goal line x — blocks past this are "in the goal". Wide enough to be visually dominant.
  var GOAL_LINE_X = W - 120;
  var GOAL_TOP = GROUND_Y - 260;

  // ---- level geometry -----------------------------------------------------------------------
  //
  // Wall (near the goal):
  //   Regular boxes: brown crates — just obstacles, pushing them into the goal is harmless.
  //   Flag targets: country flags — push ALL of these into the goal to clear.
  //   Joker boxes (level 5+): red ✕ — heavy, hard to push. If ANY enters the goal → fail.
  //
  // Field (between ball and wall):
  //   Bombs (level 2+): black spheres — when the ball (or a block) hits one, it detonates
  //   and blasts all nearby bodies toward the goal. Strategic: aim at a bomb to chain-push
  //   distant flags you can't reach with a direct kick.
  //
  var BOMB_RADIUS = CRATE / 2 - 2, BLAST_RADIUS = 250, BLAST_FORCE = 0.8;
  var explosions = []; // { x, y, r, maxR, alpha } — shockwave rings
  var fires = [];      // { x, y, vx, vy, size, alpha, color } — rising flame particles

  // ---- formation generators ----------------------------------------------------------------
  // Each returns an array of {x, y} world positions (block centres). The level picks a formation,
  // then assigns block types (flag/bomb/joker/crate) with placement rules.
  var S = CRATE + 2; // grid step

  function fmSingleStack(cols, rows, bx) {
    var out = [];
    for (var c = 0; c < cols; c++) for (var r = 0; r < rows; r++)
      out.push({ x: bx + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    return out;
  }
  function fmPyramid(baseW, bx) {
    var out = [];
    for (var r = 0; r < baseW; r++) {
      var w = baseW - r;
      var ox = bx + r * S / 2;
      for (var c = 0; c < w; c++)
        out.push({ x: ox + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    }
    return out;
  }
  function fmInvertedPyramid(baseW, bx) {
    var out = [];
    for (var r = 0; r < baseW; r++) {
      var w = Math.min(r + 1, baseW);
      var ox = bx + (baseW - w) * S / 2;
      for (var c = 0; c < w; c++)
        out.push({ x: ox + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    }
    return out;
  }
  function fmTwoClusters(cols1, rows1, cols2, rows2, bx, gap) {
    var out = [];
    for (var c = 0; c < cols1; c++) for (var r = 0; r < rows1; r++)
      out.push({ x: bx + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    var bx2 = bx + (cols1 + gap) * S;
    for (var c = 0; c < cols2; c++) for (var r = 0; r < rows2; r++)
      out.push({ x: bx2 + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: cols1 + gap + c, row: r });
    return out;
  }
  function fmLShape(w, h, bx) {
    var out = [];
    for (var c = 0; c < w; c++)
      out.push({ x: bx + c * S, y: GROUND_Y - CRATE / 2, col: c, row: 0 });
    for (var r = 1; r < h; r++)
      out.push({ x: bx, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: 0, row: r });
    return out;
  }
  function fmBridge(w, h, gapStart, gapEnd, bx) {
    var out = [];
    for (var c = 0; c < w; c++) for (var r = 0; r < h; r++) {
      if (r === 0 && c >= gapStart && c < gapEnd) continue; // gap in bottom row
      out.push({ x: bx + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    }
    return out;
  }
  function fmScattered(count, bx, spread) {
    var out = [], seed = count * 31 + 7;
    for (var i = 0; i < count; i++) {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      var c = Math.floor((seed % 1000) / 1000 * spread);
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      var r = Math.floor((seed % 1000) / 1000 * 4);
      out.push({ x: bx + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    }
    return out;
  }
  function fmFortress(w, h, bx) {
    var out = [];
    for (var c = 0; c < w; c++) for (var r = 0; r < h; r++) {
      var isInner = c > 0 && c < w - 1 && r > 0 && r < h - 1;
      if (!isInner) out.push({ x: bx + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    }
    return out;
  }
  // Three separate piles at different distances from goal
  function fmThreePiles(s1, s2, s3, bx) {
    var out = [];
    for (var c = 0; c < s1[0]; c++) for (var r = 0; r < s1[1]; r++)
      out.push({ x: bx + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    var bx2 = bx + (s1[0] + 2) * S;
    for (var c = 0; c < s2[0]; c++) for (var r = 0; r < s2[1]; r++)
      out.push({ x: bx2 + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: s1[0] + 2 + c, row: r });
    var bx3 = bx2 + (s2[0] + 2) * S;
    for (var c = 0; c < s3[0]; c++) for (var r = 0; r < s3[1]; r++)
      out.push({ x: bx3 + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: s1[0] + s2[0] + 4 + c, row: r });
    return out;
  }
  // Blocks on a floating platform (placed on a static body mid-air)
  function fmFloating(cols, rows, bx, floorY) {
    var out = [];
    for (var c = 0; c < cols; c++) for (var r = 0; r < rows; r++)
      out.push({ x: bx + c * S, y: floorY - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    return out;
  }
  // Hollow building — outer shell with an open interior
  function fmHollowBuilding(w, h, bx) {
    var out = [];
    for (var c = 0; c < w; c++) for (var r = 0; r < h; r++) {
      if (c === 0 || c === w - 1 || r === 0 || r === h - 1) // walls + floor + roof
        out.push({ x: bx + c * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: c, row: r });
    }
    return out;
  }
  // Staircase — ascending steps toward the goal
  function fmStaircase(steps, bx) {
    var out = [];
    for (var s = 0; s < steps; s++) {
      var h = s + 1;
      for (var r = 0; r < h; r++)
        out.push({ x: bx + s * S, y: GROUND_Y - CRATE / 2 - r * (CRATE + 1), col: s, row: r });
    }
    return out;
  }

  function buildLevel(n) {
    crates.forEach(function (c) { World.remove(world, c); });
    bombs.forEach(function (c) { World.remove(world, c); });
    platforms.forEach(function (p) { World.remove(world, p); });
    crates = []; targets = []; jokers = []; bombs = []; explosions = []; fires = [];
    platforms = []; fireZones = []; windZones = [];

    // ---- difficulty ramp (scales by campaign PROGRESS, not raw level) ----
    // Using progress = n / levelCount means any campaign length traverses the full curve and ends at
    // the tier-4 peak. A 50-level run is therefore the SAME challenge as the old 100-level one, just
    // tighter. (At levelCount 100 the maths below is identical to the previous raw-n version.)
    var lc = cfg.levelCount || 100;
    var prog = n / lc; // 0..1 progress through the campaign
    var tier = prog <= 0.1 ? 0 : (prog <= 0.25 ? 1 : (prog <= 0.5 ? 2 : (prog <= 0.75 ? 3 : 4)));
    var isBoss = n % 10 === 0, isMiniBoss = n % 5 === 0 && !isBoss;
    var seed = n * 13 + 7;
    function rng() { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return (seed % 10000) / 10000; }

    var slots;
    // Hash the level number so no two adjacent levels pick the same formation
    var formHash = ((n * 2654435761) >>> 0) % 1000; // Knuth multiplicative hash
    var formType = formHash % 11;
    var goalGap = Math.floor(rng() * 2) * S + S;
    // Boss/mini-boss levels scale up the wall size
    var bossScale = isBoss ? 1.5 : (isMiniBoss ? 1.25 : 1);
    // Size params scale with progress + boss (prog*5 == n/20 and prog*100/15 == n/15 at 100 levels)
    var sizeBase = Math.floor(2 + prog * 5 * bossScale);
    var sizeH = Math.floor(3 + prog * (100 / 15) * bossScale);

    if (tier === 0) {
      var cols = Math.min(1 + Math.floor(n / 4), isBoss ? 4 : 3);
      var rows = Math.min(2 + Math.floor(n / 3), isBoss ? 5 : 4);
      if (n <= 3) slots = fmSingleStack(1, 2 + n - 1, GOAL_LINE_X - S - goalGap);
      else if (isMiniBoss) slots = fmPyramid(3, GOAL_LINE_X - 3 * S - goalGap);
      else if (isBoss) slots = fmPyramid(4, GOAL_LINE_X - 4 * S - goalGap);
      else slots = fmSingleStack(cols, rows, GOAL_LINE_X - cols * S - goalGap);
    } else if (tier === 1) {
      var t1w = Math.floor(2 + rng() * 2 + (isMiniBoss ? 1 : 0) + (isBoss ? 2 : 0));
      if (isBoss) slots = fmTwoClusters(2, 4, 2, 3, GOAL_LINE_X - 7 * S - goalGap, 2);
      else if (isMiniBoss) slots = fmBridge(t1w, 3 + Math.floor(rng()), 1, t1w - 1, GOAL_LINE_X - t1w * S - goalGap);
      else if (formType < 3) slots = fmPyramid(t1w, GOAL_LINE_X - t1w * S - goalGap);
      else if (formType < 5) slots = fmLShape(t1w, 3 + Math.floor(rng() * 2), GOAL_LINE_X - 4 * S - goalGap);
      else if (formType < 7) slots = fmStaircase(3 + Math.floor(rng() * 2), GOAL_LINE_X - 5 * S - goalGap);
      else if (formType < 9) slots = fmInvertedPyramid(t1w, GOAL_LINE_X - t1w * S - goalGap);
      else slots = fmSingleStack(t1w, 3 + Math.floor(rng() * 2), GOAL_LINE_X - t1w * S - goalGap);
    } else if (tier === 2) {
      var t2w = Math.min(sizeBase, 5), t2h = Math.min(sizeH, 6);
      if (isBoss) {
        // Boss: big hollow building + floating platform
        var floorY = GROUND_Y - 6 * (CRATE + 1);
        var platX = GOAL_LINE_X - 4 * S - goalGap;
        var plat = Bodies.rectangle(platX + 2 * S, floorY + CRATE / 2 + 4, 3 * S + 20, 10, { isStatic: true, label: 'platform' });
        platforms.push(plat); World.add(world, plat);
        slots = fmFloating(3, 2, platX, floorY);
        slots = slots.concat(fmHollowBuilding(4, 4, GOAL_LINE_X - 5 * S - goalGap));
      } else if (isMiniBoss) {
        slots = fmThreePiles([2, t2h - 1], [1, t2h], [2, t2h - 1], GOAL_LINE_X - 12 * S - goalGap);
      } else if (formType < 2) {
        slots = fmTwoClusters(t2w - 1, t2h, t2w - 1, t2h - 1,
          GOAL_LINE_X - (t2w * 2 + 2) * S - goalGap, 1 + Math.floor(rng() * 2));
      } else if (formType < 3) {
        slots = fmBridge(t2w + 1, t2h, Math.floor(t2w / 2), Math.floor(t2w / 2) + 1,
          GOAL_LINE_X - (t2w + 1) * S - goalGap);
      } else if (formType < 4) {
        slots = fmHollowBuilding(t2w, t2h, GOAL_LINE_X - t2w * S - goalGap);
      } else if (formType < 5) {
        slots = fmThreePiles([1, t2h], [1, t2h - 1], [2, t2h], GOAL_LINE_X - 10 * S - goalGap);
      } else if (formType < 6) {
        slots = fmInvertedPyramid(t2w, GOAL_LINE_X - t2w * S - goalGap);
      } else if (formType < 7) {
        slots = fmStaircase(t2w + 1, GOAL_LINE_X - (t2w + 2) * S - goalGap);
      } else if (formType < 8) {
        slots = fmPyramid(t2w + 1, GOAL_LINE_X - (t2w + 1) * S - goalGap);
      } else if (formType < 9) {
        slots = fmFortress(t2w, t2h, GOAL_LINE_X - t2w * S - goalGap);
      } else {
        var floorY = GROUND_Y - 5 * (CRATE + 1);
        var platX = GOAL_LINE_X - 4 * S - goalGap;
        var plat = Bodies.rectangle(platX + 2 * S, floorY + CRATE / 2 + 4, 3 * S + 20, 10, { isStatic: true, label: 'platform' });
        platforms.push(plat); World.add(world, plat);
        slots = fmFloating(3, 2, platX, floorY);
        slots = slots.concat(fmSingleStack(2, 3, GOAL_LINE_X - 2 * S - goalGap));
      }
    } else if (tier === 3) {
      if (formType < 2) {
        slots = fmFortress(4 + Math.floor(rng()), 4 + Math.floor(rng() * 2), GOAL_LINE_X - 5 * S - goalGap);
      } else if (formType < 4) {
        slots = fmHollowBuilding(5, 5 + Math.floor(rng() * 2), GOAL_LINE_X - 6 * S - goalGap);
      } else if (formType < 6) {
        slots = fmThreePiles([2, 3], [1, 4], [2, 3], GOAL_LINE_X - 12 * S - goalGap);
      } else if (formType < 8) {
        // Floating + ground combo
        var floorY = GROUND_Y - 6 * (CRATE + 1);
        var platX = GOAL_LINE_X - 5 * S - goalGap;
        var platW = 4 * S;
        var plat = Bodies.rectangle(platX + platW / 2, floorY + CRATE / 2 + 4, platW + 20, 10, { isStatic: true, label: 'platform' });
        platforms.push(plat); World.add(world, plat);
        slots = fmFloating(3, 2, platX, floorY);
        slots = slots.concat(fmPyramid(3, GOAL_LINE_X - 4 * S - goalGap));
      } else {
        slots = fmStaircase(5 + Math.floor(rng() * 2), GOAL_LINE_X - 7 * S - goalGap);
      }
      // Wind zone (40% chance)
      if (rng() < 0.4) {
        var wzx = 600 + rng() * 200;
        windZones.push({ x: wzx, y: GROUND_Y - 300, w: 120, h: 300, force: (rng() < 0.5 ? -1 : 1) * (0.001 + rng() * 0.0008) });
      }
      // Fire zone (50% chance)
      // Fire placed after blocks (see end of buildLevel)
    } else {
      // tier 4 (76-100): master — hardest formations + multiple hazards
      if (formType < 2) {
        slots = fmFortress(5 + Math.floor(rng()), 5 + Math.floor(rng() * 2), GOAL_LINE_X - 6 * S - goalGap);
      } else if (formType < 3) {
        slots = fmHollowBuilding(6, 6, GOAL_LINE_X - 7 * S - goalGap);
      } else if (formType < 5) {
        // Multi-level: floating + hollow + ground
        var floorY = GROUND_Y - 7 * (CRATE + 1);
        var platX = GOAL_LINE_X - 6 * S - goalGap;
        var platW = 3 * S;
        var plat = Bodies.rectangle(platX + platW / 2, floorY + CRATE / 2 + 4, platW + 20, 10, { isStatic: true, label: 'platform' });
        platforms.push(plat); World.add(world, plat);
        slots = fmFloating(3, 2, platX, floorY);
        slots = slots.concat(fmHollowBuilding(4, 4, GOAL_LINE_X - 5 * S - goalGap));
      } else if (formType < 7) {
        slots = fmThreePiles([2, 4], [2, 5], [3, 4], GOAL_LINE_X - 14 * S - goalGap);
      } else {
        // Massive bridge + floating
        var bw = 6;
        slots = fmBridge(bw, 5, 2, 4, GOAL_LINE_X - bw * S - goalGap);
        var floorY = GROUND_Y - 8 * (CRATE + 1);
        var platX = GOAL_LINE_X - 4 * S - goalGap;
        var plat = Bodies.rectangle(platX + 2 * S, floorY + CRATE / 2 + 4, 3 * S + 20, 10, { isStatic: true, label: 'platform' });
        platforms.push(plat); World.add(world, plat);
        slots = slots.concat(fmFloating(2, 2, platX, floorY));
      }
      // Wind zone (70% chance, stronger)
      if (rng() < 0.7) {
        var wzx = 500 + rng() * 300;
        windZones.push({ x: wzx, y: GROUND_Y - 350, w: 130, h: 350, force: (rng() < 0.5 ? -1 : 1) * (0.0012 + rng() * 0.001) });
      }
      // Fire zone (60% chance)
      // Fire placed after blocks (see end of buildLevel)
      // Second wind zone on levels 90+ (40%)
      if (n >= 90 && rng() < 0.4) {
        var wzx2 = 350 + rng() * 200;
        windZones.push({ x: wzx2, y: GROUND_Y - 280, w: 100, h: 280, force: (rng() < 0.5 ? -1 : 1) * 0.0015 });
      }
    }

    var total = slots.length;

    // ---- block-type counts (scaled by campaign progress) ----
    // nEff maps progress onto the original 100-step curve so hazard unlocks/counts compress with the
    // campaign length (nEff === n at 100 levels; hazards arrive proportionally earlier at 50).
    var nEff = prog * 100;
    // Boss levels get more flags (harder), mini-bosses get moderate bump
    var flagPct = 0.25 + rng() * 0.15 + (isBoss ? 0.15 : 0) + (isMiniBoss ? 0.08 : 0);
    var numFlags = Math.max(1, Math.min(Math.floor(total * flagPct), total - 1));
    var numBombs = nEff >= 5 ? Math.min(1 + Math.floor(nEff / 12), Math.floor(total * 0.2), 4) : 0;
    var numJokers = nEff >= 15 ? Math.min(1 + Math.floor((nEff - 14) / 15) + (isBoss ? 1 : 0), Math.floor(total * 0.15), 3) : 0;
    var numRegular = total - numFlags - numBombs - numJokers;
    while (numRegular < 0) {
      if (numBombs > 0) { numBombs--; numRegular++; }
      else if (numJokers > 0) { numJokers--; numRegular++; }
      else { numFlags--; numRegular++; }
    }

    // ---- assign types to slots with placement rules ----
    // Sort slots: rightmost columns first (goal-side) for flags, leftmost for jokers
    var sorted = slots.slice().sort(function (a, b) { return b.x - a.x || a.row - b.row; });
    var assigned = new Array(total);
    for (var i = 0; i < total; i++) assigned[i] = 'box';

    // Flags: rightmost slots (closest to goal)
    for (var f = 0; f < numFlags && f < sorted.length; f++) {
      assigned[slots.indexOf(sorted[f])] = 'flag';
    }
    // Jokers: leftmost + highest slots (farthest from goal)
    var leftSorted = slots.slice().sort(function (a, b) { return a.x - b.x || b.row - a.row; });
    var jp = 0;
    for (var j = 0; j < leftSorted.length && jp < numJokers; j++) {
      var idx = slots.indexOf(leftSorted[j]);
      if (assigned[idx] === 'box') { assigned[idx] = 'joker'; jp++; }
    }
    // Bombs: middle slots
    var midSorted = slots.slice().sort(function (a, b) {
      var midX = (slots[0].x + slots[slots.length - 1].x) / 2;
      return Math.abs(a.x - midX) - Math.abs(b.x - midX);
    });
    var bp = 0;
    for (var bi = 0; bi < midSorted.length && bp < numBombs; bi++) {
      var idx = slots.indexOf(midSorted[bi]);
      if (assigned[idx] === 'box') { assigned[idx] = 'bomb'; bp++; }
    }

    // ---- pick flags ----
    var flagSeed = n * 7 + 3;
    var flagPool = FLAGS.slice().sort(function (a, b) { return ((flagSeed * 31 + FLAGS.indexOf(a)) % 17) - ((flagSeed * 31 + FLAGS.indexOf(b)) % 17); });
    var fi = 0;

    // ---- spawn bodies ----
    for (var si = 0; si < slots.length; si++) {
      var pos = slots[si], kind = assigned[si];
      var x = pos.x, y = pos.y;
      if (kind === 'bomb') {
        var bomb = Bodies.rectangle(x, y, CRATE, CRATE, {
          density: 0.006, friction: 0.5, restitution: 0.1, label: 'bomb',
        });
        bomb.__kind = 'bomb'; bomb.__detonated = false;
        bombs.push(bomb); crates.push(bomb); World.add(world, bomb);
      } else {
        var dens = kind === 'joker' ? 0.012 : (kind === 'box' ? 0.004 : 0.002);
        var b = Bodies.rectangle(x, y, CRATE, CRATE, {
          density: dens, friction: 0.3, restitution: 0.3, label: kind
        });
        b.__kind = kind;
        if (kind === 'flag') {
          b.__flag = flagPool[fi % flagPool.length]; fi++;
          b.__target = true; targets.push(b);
        } else if (kind === 'joker') {
          b.__joker = true; jokers.push(b);
        }
        crates.push(b); World.add(world, b);
      }
    }
    if (!targets.length && crates.length) {
      var last = crates[crates.length - 1];
      last.__kind = 'flag'; last.__flag = flagPool[0]; last.__target = true; targets.push(last);
    }

    // ---- fire zone: placed AFTER blocks, in the gap between rightmost block and goal line ----
    // Never overlaps with any block's starting position.
    var fireChance = n >= 35 ? (tier >= 4 ? 0.6 : (tier >= 3 ? 0.5 : 0.3)) : 0;
    if (rng() < fireChance) {
      var rightmostX = 0;
      for (var ri = 0; ri < crates.length; ri++) {
        if (crates[ri].position.x > rightmostX) rightmostX = crates[ri].position.x;
      }
      var fireGapStart = rightmostX + CRATE / 2 + 20; // 20px clear of the rightmost block
      var fireGapEnd = GOAL_LINE_X - 10;
      var fireSpace = fireGapEnd - fireGapStart;
      if (fireSpace > 50) { // only place fire if there's actually a gap
        var fzw = Math.min(fireSpace * 0.7, 120);
        var fzx = fireGapStart + (fireSpace - fzw) / 2; // centred in the gap
        fireZones.push({ x: fzx, y: GROUND_Y, w: fzw });
      }
    }
  }

  // ---- bomb detonation --------------------------------------------------------------------
  function detonateBomb(bomb) {
    if (bomb.__detonated) return;
    bomb.__detonated = true;
    sfx.boom();

    var bx = bomb.position.x, by = bomb.position.y;
    explosions.push({ x: bx, y: by, r: 10, maxR: BLAST_RADIUS, alpha: 1 });
    setTimeout(function () { explosions.push({ x: bx + 20, y: by - 15, r: 5, maxR: BLAST_RADIUS * 0.6, alpha: 0.8 }); }, 80);
    setTimeout(function () { explosions.push({ x: bx - 15, y: by + 10, r: 5, maxR: BLAST_RADIUS * 0.5, alpha: 0.7 }); }, 160);
    // Spawn fire particles — rising, flickering flames
    var fireColors = ['#FF4500','#FF6B00','#FF8C00','#FFA500','#FFD700','#FFEC8B'];
    for (var fi = 0; fi < 30; fi++) {
      var angle = Math.random() * Math.PI * 2;
      var speed = 1 + Math.random() * 3;
      fires.push({
        x: bx + (Math.random() - 0.5) * 40,
        y: by + (Math.random() - 0.5) * 40,
        vx: Math.cos(angle) * speed,
        vy: -2 - Math.random() * 4, // rise upward
        size: 6 + Math.random() * 12,
        alpha: 0.9 + Math.random() * 0.1,
        color: fireColors[Math.floor(Math.random() * fireColors.length)],
      });
    }

    // Blast: launch every nearby body outward with real velocity — not a gentle force
    var allBodies = crates.concat(ball ? [ball] : []);
    var chainQueue = [];
    for (var i = 0; i < allBodies.length; i++) {
      var b = allBodies[i];
      if (b === bomb) continue;
      var dx = b.position.x - bx, dy = b.position.y - by;
      var dist = Math.hypot(dx, dy);
      if (dist < BLAST_RADIUS && dist > 1) {
        var strength = BLAST_FORCE * (1 - dist / BLAST_RADIUS);
        var vx = (dx / dist) * strength * 30;
        var vy = (dy / dist) * strength * 30 - strength * 12; // bias upward
        Body.setVelocity(b, { x: b.velocity.x + vx, y: b.velocity.y + vy });
        Body.setAngularVelocity(b, (Math.random() - 0.5) * 0.3);
        // Chain-detonate other bombs caught in the blast
        if (b.label === 'bomb' && !b.__detonated) chainQueue.push(b);
      }
    }
    // Stagger chain detonations for dramatic cascading explosions
    for (var ci = 0; ci < chainQueue.length; ci++) {
      (function (chainBomb, delay) {
        setTimeout(function () { detonateBomb(chainBomb); }, delay);
      })(chainQueue[ci], 150 + ci * 120);
    }

    // Remove the bomb body after a frame so it doesn't block anything
    setTimeout(function () { World.remove(world, bomb); }, 50);
  }

  function spawnBall() {
    if (ball) World.remove(world, ball);
    ball = Bodies.circle(KICK.x, KICK.y, 22, {
      density: 0.04, friction: 0.3, frictionAir: BALL_AIR, restitution: 0.5, label: 'ball', sleepThreshold: 30,
    });
    World.add(world, ball);
    launched = false; settling = false; aiming = false; aimFrom = aimTo = null;
  }

  function startLevel(n) {
    level = n; ballsLeft = cfg.ballsPerLevel; levelOver = false;
    buildLevel(n); spawnBall(); renderHud();
  }

  // ---- win / lose detection ----------------------------------------------------------------
  // All we care about is the x position: a block is "in the goal" once its centre crosses the goal
  // line. Once crossed it LATCHES (__scored) so physics jostle can't un-score it and rerun the level.
  // Pure x-axis test: the block's centre has passed the goalpost to the right. Y is irrelevant — a block
  // that sails over the crossbar and lands behind the line still counts.
  function pastGoalLine(b) { return b.position.x > GOAL_LINE_X; }
  function inGoal(b) {
    if (b.__scored) return true;
    if (pastGoalLine(b)) { b.__scored = true; return true; }
    return false;
  }
  // A flag is "lost" if it fell off the left of the pitch (can never reach the goal). A scored flag
  // is never lost.
  function flagLost(b) {
    return !b.__scored && b.position.x < -60;
  }
  function allFlagsScored() { return targets.every(function (t) { return inGoal(t) || t.__burning; }); }
  function anyFlagLost() { return targets.some(function (t) { return flagLost(t) && !inGoal(t); }); }
  // Any ✕ joker that passes the goalpost to the right (x-axis) fails the level — pure x, no y bound.
  function anyJokerScored() { return jokers.some(pastGoalLine); }
  function bodyResting(b) { return Math.abs(b.velocity.x) < 0.25 && Math.abs(b.velocity.y) < 0.25 && Math.abs(b.angularVelocity) < 0.05; }

  function onLevelCleared() {
    if (levelOver) return; levelOver = true; sfx.clear();
    var stars = ballsLeft >= 2 ? 3 : (ballsLeft === 1 ? 2 : 1);
    var inCooldown = cooldownUntil > Date.now();
    var reward = rewardForLevel(level);
    var earned = (!inCooldown && coinsEarnedSession < SESSION_COIN_CAP) ? reward : 0;
    totalCoins += earned; coinsEarnedSession += earned;
    document.getElementById('coin-count').textContent = totalCoins;
    if (coinsEarnedSession >= SESSION_COIN_CAP && !inCooldown) {
      cooldownUntil = Date.now() + COOLDOWN_MS;
    }
    var msg = 'Level ' + level + ' cleared  ' + '★'.repeat(stars);
    if (isBossLevel(level)) msg = '🏆 BOSS Level ' + level + ' cleared!  ' + '★'.repeat(stars);
    if (earned > 0) msg += '  +' + earned + ' coins';
    else if (inCooldown) msg += '  ⏳ cooldown';
    else msg += '  (session cap reached)';
    toast(msg);
    post({ type: 'level-clear', level: level, ballsLeft: ballsLeft, stars: stars });
    setTimeout(function () {
      if (level >= cfg.levelCount) {
        campaignDone = true;
        post({ type: 'complete', level: level });
        showCampaignComplete();
      } else startLevel(level + 1);
    }, 1200);
  }

  function chargeRetry() {
    totalCoins = Math.max(0, totalCoins - RETRY_COST);
    document.getElementById('coin-count').textContent = totalCoins;
    post({ type: 'retry-charge', level: level, cost: RETRY_COST });
  }

  function onJokerFail() {
    if (levelOver) return; levelOver = true; sfx.fail();
    toast('Joker in the goal! -' + RETRY_COST + ' coins');
    chargeRetry();
    post({ type: 'level-failed', level: level });
    setTimeout(function () { startLevel(level); }, 1400);
  }
  function onFlagLost() {
    if (levelOver) return; levelOver = true; sfx.fail();
    toast('Flag lost off the pitch! -' + RETRY_COST + ' coins');
    chargeRetry();
    post({ type: 'level-failed', level: level });
    setTimeout(function () { startLevel(level); }, 1200);
  }
  function onLevelFailed() {
    if (levelOver) return; levelOver = true; sfx.fail();
    toast('Out of balls! -' + RETRY_COST + ' coins');
    chargeRetry();
    post({ type: 'level-failed', level: level });
    setTimeout(function () { startLevel(level); }, 1200);
  }

  function showCampaignComplete() {
    document.getElementById('coins').style.display = 'flex';
    var overlay = document.createElement('div');
    overlay.id = 'campaign-done';
    overlay.innerHTML = '<div class="cd-box">'
      + '<div class="cd-title">Campaign Complete! 🏆</div>'
      + '<div class="cd-sub">You cleared all ' + cfg.levelCount + ' levels</div>'
      + '<div class="cd-coins">' + totalCoins + ' coins earned this session</div>'
      + '<div class="cd-lock">Come back later for a new run</div>'
      + '</div>';
    document.body.appendChild(overlay);
  }

  // ---- input: drag the ball back, release to launch ----------------------------------------
  function toWorld(ev) {
    var r = canvas.getBoundingClientRect();
    var p = (ev.touches && ev.touches[0]) || ev;
    return { x: (p.clientX - r.left) / r.width * W, y: (p.clientY - r.top) / r.height * H };
  }
  // Kick control: press anywhere, drag in the direction you want to strike (longer drag = more power),
  // release to kick. No pull-back — you aim the shot, you don't sling it.
  function onDown(ev) {
    if (AC && AC.state === 'suspended') AC.resume();
    if (!ball || launched || levelOver) return;
    aiming = true; aimFrom = toWorld(ev); aimTo = aimFrom; ev.preventDefault();
  }
  function onMove(ev) {
    if (!aiming) return; ev.preventDefault();
    aimTo = toWorld(ev);
  }
  function onUp() {
    if (!aiming || !ball) return;
    aiming = false;
    var k = kickVelocity();
    if (k.power < MIN_POWER) { aimFrom = aimTo = null; return; } // a tap that's too soft doesn't waste a ball
    Body.setVelocity(ball, { x: k.x, y: k.y });
    Body.setAngularVelocity(ball, k.x * 0.02);                   // a little spin off the boot
    launched = true; settling = true; framesLaunched = 0; ballsLeft -= 1; renderHud(); sfx.launch();
    aimFrom = aimTo = null;
  }
  canvas.addEventListener('touchstart', onDown, { passive: false });
  canvas.addEventListener('touchmove', onMove, { passive: false });
  canvas.addEventListener('touchend', onUp);
  canvas.addEventListener('mousedown', onDown);
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);

  Events.on(engine, 'collisionStart', function (e) {
    for (var i = 0; i < e.pairs.length; i++) {
      var p = e.pairs[i];
      // Ball hit sound
      if ((p.bodyA.label === 'ball' || p.bodyB.label === 'ball') && p.collision.depth > 1) sfx.hit();
      // Bomb detonation — only the ball hitting a bomb triggers it (not blocks settling)
      var isBallA = p.bodyA.label === 'ball', isBallB = p.bodyB.label === 'ball';
      if (isBallA && p.bodyB.label === 'bomb' && !p.bodyB.__detonated) detonateBomb(p.bodyB);
      if (isBallB && p.bodyA.label === 'bomb' && !p.bodyA.__detonated) detonateBomb(p.bodyA);
    }
  });

  // ---- post-launch resolution: clear, next ball, or fail -----------------------------------
  // Fire zone hit: a block's centre is inside the fire strip (any height within CRATE of ground)
  function blockInFire(b) {
    for (var fz = 0; fz < fireZones.length; fz++) {
      var z = fireZones[fz];
      if (b.position.x > z.x && b.position.x < z.x + z.w && b.position.y > GROUND_Y - CRATE * 2) return true;
    }
    return false;
  }

  // Wind: strong enough to visibly deflect the ball and push blocks
  function applyWindForces() {
    if (!windZones.length) return;
    var allBodies = crates.concat(ball ? [ball] : []);
    for (var wi = 0; wi < windZones.length; wi++) {
      var wz = windZones[wi];
      for (var bi = 0; bi < allBodies.length; bi++) {
        var b = allBodies[bi];
        if (b.isStatic) continue;
        if (b.position.x > wz.x && b.position.x < wz.x + wz.w &&
            b.position.y > wz.y && b.position.y < wz.y + wz.h) {
          // Use setVelocity nudge for immediate visible effect (applyForce was too subtle)
          Body.setVelocity(b, { x: b.velocity.x + wz.force * 800, y: b.velocity.y });
        }
      }
    }
  }

  function resolve() {
    if (!launched || levelOver) return;
    framesLaunched++;
    // Wind pushes bodies each physics frame
    applyWindForces();
    // Fire destroys any flag that touches the fire strip — with a burning visual
    for (var ft = 0; ft < targets.length; ft++) {
      if (blockInFire(targets[ft]) && !targets[ft].__burning) {
        targets[ft].__burning = true;
        sfx.fail();
        toast('🔥 Flag burned in the fire!');
        // Spawn burning particles on the flag
        var bx = targets[ft].position.x, by = targets[ft].position.y;
        var burnColors = ['#FF4500','#FF6B00','#FF8C00','#FFA500','#FFD700','#FF0000'];
        for (var bp = 0; bp < 20; bp++) {
          fires.push({
            x: bx + (Math.random() - 0.5) * CRATE,
            y: by + (Math.random() - 0.5) * CRATE,
            vx: (Math.random() - 0.5) * 2,
            vy: -1.5 - Math.random() * 3,
            size: 5 + Math.random() * 10,
            alpha: 1,
            color: burnColors[Math.floor(Math.random() * burnColors.length)],
          });
        }
        // Delay the fail so the player sees the burn animation
        levelOver = true;
        setTimeout(function () {
          chargeRetry();
          post({ type: 'level-failed', level: level });
          setTimeout(function () { startLevel(level); }, 800);
        }, 1200);
        return;
      }
    }
    if (anyJokerScored()) { onJokerFail(); return; }
    if (framesLaunched > 45 && anyFlagLost()) { onFlagLost(); return; }
    if (allFlagsScored()) { onLevelCleared(); return; }
    var ballRested = ball && (bodyResting(ball) || ball.position.y > GROUND_Y || ball.position.x > W + 60);
    if (ballRested && settling) {
      settling = false;
      setTimeout(function () {
        if (levelOver) return;
        if (anyJokerScored()) { onJokerFail(); return; }
        if (anyFlagLost()) { onFlagLost(); return; }  // ball is rested — safe to check now
        if (allFlagsScored()) { onLevelCleared(); return; }
        if (ballsLeft <= 0) onLevelFailed();
        else spawnBall();
      }, 500);
    }
  }

  // ---- render ------------------------------------------------------------------------------
  function drawBlock(b) {
    if (b.__kind === 'bomb') {
      if (b.__detonated) return;
      var s = CRATE, h = s / 2;
      ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
      // Dark square background with orange border — stands out in the wall
      var bg = ctx.createLinearGradient(-h, -h, h, h);
      bg.addColorStop(0, '#78350F'); bg.addColorStop(1, '#1C0A00');
      ctx.fillStyle = bg; ctx.fillRect(-h, -h, s, s);
      // Orange border
      ctx.strokeStyle = '#FB923C'; ctx.lineWidth = 3;
      ctx.strokeRect(-h + 1.5, -h + 1.5, s - 3, s - 3);
      // Bomb circle in centre
      var br = h * 0.55;
      ctx.fillStyle = '#111827'; ctx.beginPath(); ctx.arc(0, 2, br, 0, 7); ctx.fill();
      ctx.strokeStyle = '#F97316'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(0, 2, br, 0, 7); ctx.stroke();
      // Fuse + spark at top
      ctx.strokeStyle = '#A16207'; ctx.lineWidth = 2; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(3, 2 - br); ctx.quadraticCurveTo(10, -h - 2, 5, -h + 2); ctx.stroke();
      ctx.fillStyle = '#FDE68A'; ctx.beginPath(); ctx.arc(5, -h + 2, 4, 0, 7); ctx.fill();
      ctx.fillStyle = '#EF4444'; ctx.beginPath(); ctx.arc(5, -h + 2, 2.5, 0, 7); ctx.fill();
      // Star burst
      ctx.fillStyle = '#FCD34D';
      for (var si = 0; si < 6; si++) {
        var sa = si / 6 * Math.PI * 2 - Math.PI / 2;
        ctx.beginPath(); ctx.moveTo(0, 2);
        ctx.lineTo(Math.cos(sa) * 8, 2 + Math.sin(sa) * 8);
        ctx.lineTo(Math.cos(sa + 0.3) * 4, 2 + Math.sin(sa + 0.3) * 4);
        ctx.closePath(); ctx.fill();
      }
      ctx.restore();
      return;
    }
    var s = CRATE, h = s / 2;
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.beginPath(); ctx.rect(-h, -h, s, s); ctx.clip();

    if (b.__kind === 'flag') {
      var f = b.__flag;
      // Real flag art from the CDN, filling the block face. Falls back to stripes until it decodes.
      var fimg = flagImage(f.code), drewImg = false;
      if (fimg && fimg.complete && fimg.naturalWidth > 0) {
        try { ctx.drawImage(fimg, -h, -h, s, s); drewImg = true; } catch (e) { drewImg = false; }
      }
      if (!drewImg) {
        // Country flag stripes (instant placeholder shown until the real SVG loads / if it fails)
        if (f.v) { var sw = s / 3; for (var i = 0; i < 3; i++) { ctx.fillStyle = f.c[i]; ctx.fillRect(-h + i * sw, -h, sw, s); } }
        else { var sh = s / 3; for (var i = 0; i < 3; i++) { ctx.fillStyle = f.c[i]; ctx.fillRect(-h, -h + i * sh, s, sh); } }
        // Special marks for flags that need more than stripes
        if (f.mark) {
          var mc = f.c[1]; // use middle stripe color as mark color
          if (f.mark === 'O') { ctx.fillStyle = '#BC002D'; ctx.beginPath(); ctx.arc(0, 0, s * 0.18, 0, 7); ctx.fill(); }
          else if (f.mark === '+') { ctx.fillStyle = mc; ctx.fillRect(-s*0.06, -h, s*0.12, s); ctx.fillRect(-h, -s*0.06, s, s*0.12); }
          else if (f.mark === 'X') { ctx.save(); ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = s*0.1; ctx.beginPath(); ctx.moveTo(-h,-h); ctx.lineTo(h,h); ctx.moveTo(h,-h); ctx.lineTo(-h,h); ctx.stroke(); ctx.restore(); }
          else if (f.mark === 'C') { ctx.fillStyle = '#FFFFFF'; ctx.beginPath(); ctx.arc(-2, 0, s*0.14, 0, 7); ctx.fill(); ctx.fillStyle = f.c[0]; ctx.beginPath(); ctx.arc(2, 0, s*0.11, 0, 7); ctx.fill(); }
          else if (f.mark === '🍁') { ctx.fillStyle = '#FF0000'; ctx.font = (s*0.4)+'px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('🍁', 0, 0); }
        }
      }
      if (b.__burning) { ctx.fillStyle = 'rgba(0,0,0,.6)'; ctx.fillRect(-h, -h, s, s); } // darkened = burning
      else if (inGoal(b)) { ctx.fillStyle = 'rgba(110,231,183,.25)'; ctx.fillRect(-h, -h, s, s); }
    } else if (b.__kind === 'joker') {
      // Red danger block with ✕
      var jg = ctx.createLinearGradient(-h, -h, h, h);
      jg.addColorStop(0, '#7F1D1D'); jg.addColorStop(1, '#991B1B');
      ctx.fillStyle = jg; ctx.fillRect(-h, -h, s, s);
      ctx.strokeStyle = '#FCA5A5'; ctx.lineWidth = 4; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(-h + 12, -h + 12); ctx.lineTo(h - 12, h - 12); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(h - 12, -h + 12); ctx.lineTo(-h + 12, h - 12); ctx.stroke();
      if (inGoal(b)) { ctx.fillStyle = 'rgba(248,113,113,.3)'; ctx.fillRect(-h, -h, s, s); }
    } else {
      // Regular brown crate
      var cg = ctx.createLinearGradient(-h, -h, h, h);
      cg.addColorStop(0, '#A9743B'); cg.addColorStop(1, '#6B4423');
      ctx.fillStyle = cg; ctx.fillRect(-h, -h, s, s);
      ctx.strokeStyle = '#4A2F18'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(-h, -h); ctx.lineTo(h, h); ctx.moveTo(h, -h); ctx.lineTo(-h, h); ctx.stroke();
    }
    ctx.restore();
    // Border
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    ctx.strokeStyle = b.__kind === 'joker' ? 'rgba(248,113,113,.6)' : 'rgba(0,0,0,.4)';
    ctx.lineWidth = 2; ctx.strokeRect(-h, -h, s, s);
    ctx.restore();
  }
  function drawBall(b) {
    ctx.save(); ctx.translate(b.position.x, b.position.y); ctx.rotate(b.angle);
    var rg = ctx.createRadialGradient(-6, -8, 4, 0, 0, 24);
    rg.addColorStop(0, '#ffffff'); rg.addColorStop(0.7, '#e7e7e7'); rg.addColorStop(1, '#9aa0a6');
    ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(0, 0, 22, 0, 7); ctx.fill();
    ctx.fillStyle = '#1F2937'; ctx.beginPath(); ctx.arc(0, 0, 6, 0, 7); ctx.fill();
    for (var k = 0; k < 5; k++) { var a = k / 5 * Math.PI * 2; ctx.beginPath();
      ctx.arc(Math.cos(a) * 13, Math.sin(a) * 13, 3.2, 0, 7); ctx.fill(); }
    ctx.restore();
  }
  function drawGoal() {
    var gx = GOAL_LINE_X, top = GOAL_TOP, postW = 10, depth = W - gx + 10;
    ctx.save();

    // Glow behind the goal mouth — makes it pop as the target zone
    var glow = ctx.createRadialGradient(gx + depth / 2, (top + GROUND_Y) / 2, 20, gx + depth / 2, (top + GROUND_Y) / 2, depth * 1.2);
    glow.addColorStop(0, 'rgba(110,231,183,.12)'); glow.addColorStop(1, 'rgba(110,231,183,0)');
    ctx.fillStyle = glow; ctx.fillRect(gx - 30, top - 30, depth + 60, GROUND_Y - top + 60);

    // Goal-line marker on the pitch (dashed)
    ctx.setLineDash([10, 7]);
    ctx.strokeStyle = 'rgba(255,255,255,.3)'; ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.moveTo(gx, top - 6); ctx.lineTo(gx, GROUND_Y + 4); ctx.stroke();
    ctx.setLineDash([]);

    // Net background — subtle dark fill so you can see things inside the goal
    ctx.fillStyle = 'rgba(0,0,0,.2)';
    ctx.fillRect(gx, top, depth, GROUND_Y - top);

    // Net mesh — denser, slightly brighter
    ctx.strokeStyle = 'rgba(255,255,255,.18)'; ctx.lineWidth = 1;
    for (var nx = gx; nx <= W; nx += 9) { ctx.beginPath(); ctx.moveTo(nx, top); ctx.lineTo(nx, GROUND_Y); ctx.stroke(); }
    for (var ny = top; ny <= GROUND_Y; ny += 9) { ctx.beginPath(); ctx.moveTo(gx, ny); ctx.lineTo(W, ny); ctx.stroke(); }

    // Posts + crossbar — thick white with a subtle gradient
    var postGrad = ctx.createLinearGradient(gx - postW, 0, gx, 0);
    postGrad.addColorStop(0, '#D1D5DB'); postGrad.addColorStop(0.5, '#F9FAFB'); postGrad.addColorStop(1, '#E5E7EB');
    ctx.fillStyle = postGrad;
    // Left post
    ctx.fillRect(gx - postW, top - 4, postW, GROUND_Y - top + 4);
    // Crossbar
    ctx.fillRect(gx - postW, top - 4, depth + postW, postW + 2);
    // Post highlights (shine)
    ctx.fillStyle = 'rgba(255,255,255,.5)';
    ctx.fillRect(gx - postW + 2, top, 3, GROUND_Y - top);
    ctx.fillRect(gx - postW + 2, top - 2, depth + postW - 4, 2);
    // Post shadow
    ctx.fillStyle = 'rgba(0,0,0,.15)';
    ctx.fillRect(gx - postW, GROUND_Y - 2, postW, 4);

    ctx.restore();
  }
  function drawKickSpot() {
    // penalty-spot mark + a faint aim-ring under the resting ball
    ctx.fillStyle = 'rgba(255,255,255,.5)';
    ctx.beginPath(); ctx.ellipse(KICK.x, GROUND_Y - 4, 9, 3.5, 0, 0, 7); ctx.fill();
  }
  function drawAim() {
    if (!aiming || !ball) return;
    var k = kickVelocity();
    var px = ball.position.x, py = ball.position.y;
    // Predicted trajectory — forward-simulate with the SAME per-step rule Matter uses, so the dotted
    // line and the ball follow the same curve: v(n) = v(n-1)*(1-frictionAir) + gravity*scale*dt², pos += v.
    var fa = 1 - BALL_AIR;
    var gStep = engine.gravity.y * engine.gravity.scale * FIXED_DT * FIXED_DT;
    var vx = k.x, vy = k.y, x = px, y = py;
    ctx.fillStyle = 'rgba(255,255,255,.7)';
    for (var i = 1; i <= 110; i++) {
      vx *= fa; vy = vy * fa + gStep; x += vx; y += vy;
      if (y > GROUND_Y || x > W || x < 0) break;
      if (i % 6 === 0) { ctx.beginPath(); ctx.arc(x, y, 3.4, 0, 7); ctx.fill(); }
    }
    // direction arrow off the ball
    if (k.power > 0) {
      var d = Math.hypot(k.x, k.y) || 1, ux = k.x / d, uy = k.y / d, len = 34 + k.power * 70;
      var ex = px + ux * len, ey = py + uy * len;
      ctx.strokeStyle = '#FB923C'; ctx.lineWidth = 5; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(ex, ey); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(ex, ey);
      ctx.lineTo(ex - (ux * 14 + uy * 9), ey - (uy * 14 - ux * 9));
      ctx.lineTo(ex - (ux * 14 - uy * 9), ey - (uy * 14 + ux * 9));
      ctx.closePath(); ctx.fillStyle = '#FB923C'; ctx.fill();
    }
    // power meter above the ball
    var pw = 84, ph = 9, bx = px - pw / 2, by = py - 56;
    ctx.fillStyle = 'rgba(0,0,0,.45)'; ctx.fillRect(bx, by, pw, ph);
    var grad = ctx.createLinearGradient(bx, 0, bx + pw, 0);
    grad.addColorStop(0, '#6EE7B7'); grad.addColorStop(0.6, '#FCD34D'); grad.addColorStop(1, '#F87171');
    ctx.fillStyle = grad; ctx.fillRect(bx, by, pw * k.power, ph);
    ctx.strokeStyle = 'rgba(255,255,255,.6)'; ctx.lineWidth = 1.5; ctx.strokeRect(bx, by, pw, ph);
  }
  function render() {
    var sky = ctx.createLinearGradient(0, 0, 0, H);
    sky.addColorStop(0, '#1b2a4a'); sky.addColorStop(0.6, '#2c3e63'); sky.addColorStop(1, '#3a5331');
    ctx.fillStyle = sky; ctx.fillRect(0, 0, W, H);
    var gg = ctx.createLinearGradient(0, GROUND_Y, 0, H);
    gg.addColorStop(0, '#4e7a3a'); gg.addColorStop(1, '#2f4a23');
    ctx.fillStyle = gg; ctx.fillRect(0, GROUND_Y, W, H - GROUND_Y);
    ctx.fillStyle = 'rgba(0,0,0,.18)'; ctx.fillRect(0, GROUND_Y, W, 5);

    // ---- hazard rendering (behind blocks, on top of ground) ----
    // Fire zones — flickering orange/red strip on the ground
    for (var fzi = 0; fzi < fireZones.length; fzi++) {
      var fz = fireZones[fzi];
      ctx.save();
      var fg = ctx.createLinearGradient(fz.x, fz.y - 30, fz.x, fz.y);
      fg.addColorStop(0, 'rgba(239,68,68,0)');
      fg.addColorStop(0.5, 'rgba(251,146,60,0.3)');
      fg.addColorStop(1, 'rgba(239,68,68,0.5)');
      ctx.fillStyle = fg; ctx.fillRect(fz.x, fz.y - 30, fz.w, 30);
      // Flame tips (animated via frameCount)
      for (var ft = 0; ft < fz.w; ft += 8) {
        var fh = 10 + Math.sin(frameCount * 0.15 + ft * 0.5) * 8;
        ctx.fillStyle = (ft % 16 < 8) ? 'rgba(255,100,0,0.7)' : 'rgba(255,200,0,0.6)';
        ctx.fillRect(fz.x + ft, fz.y - fh, 6, fh);
      }
      // Warning label
      ctx.fillStyle = '#FCA5A5'; ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('🔥 FIRE', fz.x + fz.w / 2, fz.y - 34);
      ctx.restore();
    }
    // Wind zones — animated directional streaks
    for (var wzi = 0; wzi < windZones.length; wzi++) {
      var wz = windZones[wzi];
      ctx.save(); ctx.globalAlpha = 0.25;
      var dir = wz.force > 0 ? 1 : -1;
      for (var ws = 0; ws < 8; ws++) {
        var wy = wz.y + (wz.h * ws / 8) + ((frameCount * 2 + ws * 40) % wz.h);
        if (wy > wz.y + wz.h) wy -= wz.h;
        ctx.strokeStyle = '#93C5FD'; ctx.lineWidth = 2; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(wz.x + (dir > 0 ? 0 : wz.w), wy);
        ctx.lineTo(wz.x + (dir > 0 ? wz.w * 0.7 : wz.w * 0.3), wy); ctx.stroke();
        // Arrow head
        var ax = wz.x + (dir > 0 ? wz.w * 0.7 : wz.w * 0.3);
        ctx.beginPath(); ctx.moveTo(ax, wy); ctx.lineTo(ax - dir * 8, wy - 4); ctx.lineTo(ax - dir * 8, wy + 4);
        ctx.closePath(); ctx.fillStyle = '#93C5FD'; ctx.fill();
      }
      // Label
      ctx.globalAlpha = 0.6;
      ctx.fillStyle = '#93C5FD'; ctx.font = 'bold 11px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText('💨 WIND', wz.x + wz.w / 2, wz.y - 6);
      ctx.restore();
    }
    // Floating platforms — thick dark bars
    for (var pi = 0; pi < platforms.length; pi++) {
      var p = platforms[pi];
      ctx.save(); ctx.translate(p.position.x, p.position.y);
      var pw = p.bounds.max.x - p.bounds.min.x, ph = p.bounds.max.y - p.bounds.min.y;
      var pg = ctx.createLinearGradient(0, -ph / 2, 0, ph / 2);
      pg.addColorStop(0, '#4B5563'); pg.addColorStop(1, '#1F2937');
      ctx.fillStyle = pg; ctx.fillRect(-pw / 2, -ph / 2, pw, ph);
      ctx.fillStyle = 'rgba(255,255,255,.15)'; ctx.fillRect(-pw / 2, -ph / 2, pw, 2);
      ctx.restore();
    }

    drawGoal();
    drawKickSpot();
    crates.forEach(drawBlock);
    if (ball) drawBall(ball);
    drawAim();
    // Explosions ON TOP of everything — big, dramatic, multi-layered
    for (var ei = explosions.length - 1; ei >= 0; ei--) {
      var ex = explosions[ei];
      ex.r += 4; ex.alpha -= 0.015;
      if (ex.alpha <= 0) { explosions.splice(ei, 1); continue; }
      ctx.save(); ctx.globalAlpha = ex.alpha;
      // Outer shockwave ring
      ctx.strokeStyle = '#FB923C'; ctx.lineWidth = 8;
      ctx.beginPath(); ctx.arc(ex.x, ex.y, ex.r, 0, 7); ctx.stroke();
      // Inner fireball glow
      var fg = ctx.createRadialGradient(ex.x, ex.y, 0, ex.x, ex.y, ex.r * 0.7);
      fg.addColorStop(0, 'rgba(255,220,50,' + (ex.alpha * 0.6) + ')');
      fg.addColorStop(0.4, 'rgba(251,146,60,' + (ex.alpha * 0.4) + ')');
      fg.addColorStop(1, 'rgba(239,68,68,0)');
      ctx.fillStyle = fg; ctx.beginPath(); ctx.arc(ex.x, ex.y, ex.r * 0.7, 0, 7); ctx.fill();
      // Bright centre flash (fades faster)
      if (ex.alpha > 0.5) {
        ctx.fillStyle = 'rgba(255,255,255,' + ((ex.alpha - 0.5) * 1.5) + ')';
        ctx.beginPath(); ctx.arc(ex.x, ex.y, ex.r * 0.25, 0, 7); ctx.fill();
      }
      // Spark particles (6 debris streaks)
      ctx.strokeStyle = '#FDE68A'; ctx.lineWidth = 3; ctx.lineCap = 'round';
      for (var sp = 0; sp < 6; sp++) {
        var sa = sp / 6 * Math.PI * 2 + ex.r * 0.02;
        var sr = ex.r * 0.8;
        ctx.beginPath();
        ctx.moveTo(ex.x + Math.cos(sa) * sr * 0.6, ex.y + Math.sin(sa) * sr * 0.6);
        ctx.lineTo(ex.x + Math.cos(sa) * sr, ex.y + Math.sin(sa) * sr);
        ctx.stroke();
      }
      ctx.restore();
    }
    // Fire particles — rising, flickering, shrinking flames
    for (var fi = fires.length - 1; fi >= 0; fi--) {
      var fp = fires[fi];
      fp.x += fp.vx; fp.y += fp.vy;
      fp.vy -= 0.03; // slight upward acceleration (heat rises)
      fp.vx *= 0.98;
      fp.size *= 0.97;
      fp.alpha -= 0.012;
      if (fp.alpha <= 0 || fp.size < 1) { fires.splice(fi, 1); continue; }
      ctx.save(); ctx.globalAlpha = fp.alpha;
      // Outer glow
      ctx.shadowColor = fp.color; ctx.shadowBlur = fp.size * 2;
      ctx.fillStyle = fp.color;
      ctx.beginPath(); ctx.arc(fp.x, fp.y, fp.size, 0, 7); ctx.fill();
      // Bright core
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#FFF8DC';
      ctx.beginPath(); ctx.arc(fp.x, fp.y, fp.size * 0.35, 0, 7); ctx.fill();
      ctx.restore();
    }
  }

  // ---- hud ---------------------------------------------------------------------------------
  var elLevel = document.getElementById('level'), elBalls = document.getElementById('balls'),
      elTask = document.getElementById('task');
  function renderHud() {
    elLevel.textContent = 'Level ' + level + ' / ' + cfg.levelCount;
    var s = '';
    for (var i = 0; i < cfg.ballsPerLevel; i++) s += '<span class="dot' + (i < ballsLeft ? '' : ' used') + '"></span>';
    elBalls.innerHTML = s;
    // Task bar: show required flags as mini chips + joker warning
    var html = '<span style="margin-right:4px">Score:</span>';
    for (var t = 0; t < targets.length; t++) {
      var f = targets[t].__flag; if (!f) continue;
      var done = inGoal(targets[t]);
      html += '<div class="flag-chip' + (f.v ? ' v' : '') + (done ? ' scored' : '') + '">';
      for (var ci = 0; ci < 3; ci++) html += '<div class="stripe" style="background:' + f.c[ci] + '"></div>';
      html += '<img class="flag-chip-img" src="' + FLAG_CDN + f.code + '.svg" alt="" onerror="this.remove()" />';
      html += '</div>';
    }
    if (jokers.length > 0) html += '<span class="joker-warn">Avoid ✕ (' + jokers.length + ')</span>';
    var inCd = cooldownUntil > Date.now();
    var lvlReward = rewardForLevel(level);
    if (inCd) {
      var cdLeft = Math.ceil((cooldownUntil - Date.now()) / 60000);
      html += '<span style="color:#93C5FD;font-weight:700;margin-left:6px">⏳ ' + cdLeft + 'm</span>';
    } else if (coinsEarnedSession >= SESSION_COIN_CAP) {
      html += '<span style="color:#9CA3AF;font-weight:700;margin-left:6px">capped</span>';
    } else if (isBossLevel(level)) {
      html += '<span class="reward" style="font-size:14px">🏆 +' + lvlReward + ' 🪙</span>';
    } else {
      html += '<span class="reward">+' + lvlReward + ' 🪙</span>';
    }
    html += '<span style="color:#F87171;font-size:11px;margin-left:2px">retry -' + RETRY_COST + '</span>';
    elTask.innerHTML = html;
  }
  var elToast = document.getElementById('toast'), toastT = null;
  function toast(msg) { elToast.textContent = msg; elToast.classList.add('show');
    clearTimeout(toastT); toastT = setTimeout(function () { elToast.classList.remove('show'); }, 1400); }

  // ---- loop --------------------------------------------------------------------------------
  // Physics steps every frame at real-time speed — same trajectory/range (the aim preview is
  // predicted independently), the ball just flies at full pace rather than a floaty half-speed.
  var last = 0, frameCount = 0;
  function loop(ts) { last = ts || 1; frameCount++;
    Engine.update(engine, FIXED_DT); resolve();
    render(); if (launched) renderHud(); requestAnimationFrame(loop); }

  // ---- start hook (RN calls this once it has the day config) -------------------------------
  window.__slingshotApplyStart = function (config) {
    if (config) { cfg.levelCount = config.levelCount || cfg.levelCount;
      cfg.ballsPerLevel = config.ballsPerLevel || cfg.ballsPerLevel;
      cfg.startLevel = config.startLevel || 1; }
    startLevel(cfg.startLevel); requestAnimationFrame(loop);
  };

  post({ type: 'ready' });
  // Fallback: if RN never pushes config (e.g. Storybook), boot with defaults after a beat.
  setTimeout(function () { if (!last) window.__slingshotApplyStart(null); }, 1500);
})();
</script>
</body>
</html>`;
}
